import { useRef, useState, useCallback, useEffect } from "react";
import { fileUrl } from "@/lib/api";

/**
 * Before/after slider. Both images occupy the same box; the "after" image
 * is clipped horizontally to a percentage controlled by a draggable handle.
 * Works with pointer (mouse + touch) and falls back to a range input for a11y.
 */
export default function BeforeAfterSlider({ beforePath, afterPath, label }) {
  const boxRef = useRef(null);
  const [pct, setPct] = useState(50);
  const [dragging, setDragging] = useState(false);

  const movePct = useCallback((clientX) => {
    const el = boxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const rel = (clientX - rect.left) / rect.width;
    setPct(Math.max(0, Math.min(100, rel * 100)));
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    movePct(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    movePct(e.clientX);
  };
  const onPointerUp = (e) => {
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // keyboard a11y
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      if (document.activeElement !== el) return;
      if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 5));
      if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!beforePath || !afterPath) return null;

  return (
    <div className="flex flex-col gap-2" data-testid={`slider-${label || "pair"}`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">
        <span>{label || "Compare"}</span>
        <span className="font-mono-data">{Math.round(pct)}%</span>
      </div>
      <div
        ref={boxRef}
        tabIndex={0}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label={`Before/after comparison${label ? `: ${label}` : ""}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={`relative aspect-[4/3] w-full bg-[#1A1208] border border-[#8B6A1F]/45 rounded-sm overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/60 ${dragging ? "cursor-grabbing" : "cursor-ew-resize"}`}
      >
        {/* BEFORE on the LEFT (full image, bottom layer) */}
        <img
          src={fileUrl(beforePath)}
          alt="Before"
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          draggable={false}
        />
        {/* AFTER on the RIGHT (top layer, clipped from the left edge by pct%) */}
        <img
          src={fileUrl(afterPath)}
          alt="After"
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          draggable={false}
          style={{ clipPath: `inset(0 0 0 ${pct}%)` }}
        />
        {/* Divider line + handle */}
        <div
          className="absolute top-0 bottom-0 w-px bg-[#D4AF37] pointer-events-none"
          style={{ left: `${pct}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full bg-[#D4AF37] border-2 border-[#1A1208] shadow-[0_2px_6px_rgba(0,0,0,0.4)] pointer-events-none flex items-center justify-center"
          style={{ left: `${pct}%` }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M4 3L1 7L4 11M10 3L13 7L10 11" stroke="#1A1208" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {/* Corner labels */}
        <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] text-[#F9F0D4] bg-[#0A0604]/70 px-1.5 py-0.5 rounded-sm pointer-events-none">Before</span>
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-[0.2em] text-[#F9F0D4] bg-[#0A0604]/70 px-1.5 py-0.5 rounded-sm pointer-events-none">After</span>
      </div>
    </div>
  );
}
